import React, { useRef, useCallback, useEffect, useState } from 'react';
import useKeyPress from '../../hooks/useKeyPress';
import useClickOutside from '../../hooks/useClickOutside';
import { useTransition } from '@react-spring/web';
import {
  PopupWrapper,
  Container,
  MenuItem,
  Icon,
  Label,
  Arrow,
  PasswordInputWrapper,
  PasswordInput,
  ConfirmButton,
  MenuItemWrapper,
} from './styles';

const PopupMenu = ({
  id,
  popupIsOpen,
  onClose,
  lightMode = false,
  alignment = 'center',
  border = false,
  options = [],
}) => {
  const [top, setTop] = useState(-10000);
  const [left, setLeft] = useState(-10000);
  const [arrowLeft, setArrowLeft] = useState(0);
  const [arrowFlip, setArrowFlip] = useState(false);
  const [buttonCenter, setButtonCenter] = useState(0);
  const [minWidth, setMinWidth] = useState(-10000);
  const [password, setPassword] = useState('');

  const popup = useRef();
  const arrow = useRef();
  const input = useRef();
  const close = useCallback(onClose, []);
  const minLeft = 52;

  useKeyPress('Escape', close, popupIsOpen);
  useClickOutside(popup, close);

  useEffect(() => {
    if (popupIsOpen && popup && arrow) {
      const header = document.querySelector('#headerWrapper > section');
      const panel = document.querySelector('#panel-wrapper > div > section');
      const button = document.querySelector(`#${id}`);
      const buttonRect = button ? button.getBoundingClientRect() : {};
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const popupRect = popup.current ? popup.current.getBoundingClientRect() : {};
      const arrowRect = arrow.current ? arrow.current.getBoundingClientRect() : {};
      const headerRect = header ? header.getBoundingClientRect() : {};
      const panelRect = panel ? panel.getBoundingClientRect() : {};
      const panelScroll =
        panelRect.top !== undefined && headerRect.height !== undefined
          ? Math.abs(panelRect.top - headerRect.height)
          : 0;
      let leftPos = 0;
      if (alignment === 'left') {
        leftPos = buttonRect.left;
      } else if (alignment === 'right') {
        leftPos = buttonRect.right;
      } else {
        leftPos = buttonCenter - popupRect.width / 2;
      }
      if (input?.current?.classList?.contains('selected')) {
        leftPos = minLeft;
        setArrowLeft(buttonCenter - minLeft);
      }
      setButtonCenter(buttonCenter);
      setLeft(leftPos);
      let topPosition = buttonRect.top - popupRect.height - headerRect.height - arrowRect.height / 2 - 5 + panelScroll;
      if (topPosition < 0) {
        topPosition = buttonRect.top - arrowRect.height / 2 + panelScroll;
        setArrowFlip(true);
      } else {
        setArrowFlip(false);
      }
      setTop(topPosition);
      setMinWidth(buttonRect.width);
    }
  }, [popupIsOpen, input?.current?.classList]);

  const transitions = {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 200 },
  };

  const popupTransitions = useTransition(popupIsOpen, transitions);

  const passwordPanel = (value, onConfirm, selected) => (
    <PasswordInputWrapper className={selected ? 'selected' : ''} ref={input} selected={selected}>
      <PasswordInput
        type="text"
        title={i18n.__('node_tools_password')}
        autoFocus={selected}
        value={password || value}
        onChange={(event) => setPassword(event.target.value)}
      />
      <ConfirmButton
        title={i18n.__('app_save')}
        onClick={() => onConfirm(password)}
        selected={selected}
        disabled={!password.trim()}
      />
    </PasswordInputWrapper>
  );

  const selectItem = (tag, isPassword, onSelect) => {
    setArrowLeft(isPassword ? buttonCenter - minLeft : 0);
    onSelect(tag);
    if (!isPassword) {
      setPassword('');
    }
  };

  const menuItems = options.map((option, index) => {
    const { src, name, tag, onSelect, onConfirm, selected, value } = option;
    const isPassword = tag === 'password';
    return (
      <MenuItemWrapper key={tag + isPassword}>
        <MenuItem
          onClick={() => selectItem(tag, isPassword, onSelect)}
          $lightMode={lightMode}
          border={border}
          first={index === 0}
          selected={selected}
          isPassword={isPassword}
        >
          {src && <Icon src={src} />}
          <Label dangerouslySetInnerHTML={{ __html: name }} />
        </MenuItem>
        {isPassword && passwordPanel(value, onConfirm, selected)}
      </MenuItemWrapper>
    );
  });

  return popupTransitions(
    (style, show) =>
      show && (
        <PopupWrapper style={style} ref={popup} top={top} left={left} $minWidth={minWidth} $lightMode={lightMode}>
          <Container border={border}>{menuItems}</Container>
          <Arrow ref={arrow} left={arrowLeft} flip={arrowFlip} />
        </PopupWrapper>
      ),
  );
};

export default PopupMenu;
